import React from 'react';
import type { CubeQuerySchema } from 'shared/src/api/cube-query-schema';
import type { LabeledTableViz } from 'shared/src/api/ai-labeled-viz-types';
import { VisualizationBlock } from '@/features/ai/visualization/visualization-block';

// ─── Section A: Budget Discrepancies ───

const A1_QUERY: CubeQuerySchema = {
  dimensions: [
    'campaigns.campaign_number',
    'campaigns.campaign_name',
    'campaigns.budget',
    'campaigns.price',
    'campaigns.budget_strategy_difference'
  ],
  filters: [
    { member: 'campaigns.stage', operator: 'equals', values: ['Campaign is Live'] },
    { member: 'campaigns.budget_discrepancy_type', operator: 'equals', values: ['true_mismatch'] }
  ],
  order: [['campaigns.budget_strategy_difference', 'desc']],
  limit: 500
};

const A1_VIZ: LabeledTableViz = {
  vizType: 'table',
  title: 'Campaigns with True Budget Mismatches',
  columns: [
    { key: 'campaigns.campaign_number', label: 'Campaign #', format: 'string' },
    { key: 'campaigns.campaign_name', label: 'Campaign Name', format: 'string' },
    { key: 'campaigns.budget', label: 'Zoho Budget', format: 'currency' },
    { key: 'campaigns.price', label: 'Bravo Strategy Price', format: 'currency' },
    {
      key: 'campaigns.budget_strategy_difference',
      label: 'Budget vs Strategy Diff',
      format: 'currency'
    }
  ]
};

const A2_QUERY: CubeQuerySchema = {
  dimensions: [
    'campaigns.campaign_number',
    'campaigns.campaign_name',
    'campaigns.budget_discrepancy_type',
    'campaigns.budget',
    'campaigns.price',
    'campaigns.budget_strategy_difference',
    'campaigns.budget_line_item_difference',
    'campaigns.budget_effective_difference',
    'campaigns.referral_commission_rate'
  ],
  filters: [
    { member: 'campaigns.stage', operator: 'equals', values: ['Campaign is Live'] },
    {
      member: 'campaigns.budget_discrepancy_type',
      operator: 'equals',
      values: ['true_mismatch', 'no_line_items']
    }
  ],
  order: [['campaigns.budget_strategy_difference', 'desc']],
  limit: 500
};

const A2_VIZ: LabeledTableViz = {
  vizType: 'table',
  title: 'Budget Mismatch Details',
  columns: [
    { key: 'campaigns.campaign_number', label: 'Campaign #', format: 'string' },
    { key: 'campaigns.campaign_name', label: 'Campaign Name', format: 'string' },
    { key: 'campaigns.budget_discrepancy_type', label: 'Discrepancy Type', format: 'enum' },
    { key: 'campaigns.budget', label: 'Zoho Budget', format: 'currency' },
    { key: 'campaigns.price', label: 'Bravo Strategy Price', format: 'currency' },
    {
      key: 'campaigns.budget_strategy_difference',
      label: 'Budget vs Strategy Diff',
      format: 'currency'
    },
    {
      key: 'campaigns.budget_line_item_difference',
      label: 'Budget vs LI Diff',
      format: 'currency'
    },
    {
      key: 'campaigns.budget_effective_difference',
      label: 'Budget vs Effective Diff',
      format: 'currency'
    },
    { key: 'campaigns.referral_commission_rate', label: 'Referral Rate', format: 'percent' }
  ]
};

// ─── Section B: Line Item Discrepancies ───

const B1_QUERY: CubeQuerySchema = {
  dimensions: [
    'campaigns.campaign_number',
    'campaigns.campaign_name',
    'zoho_line_items.name',
    'zoho_line_items.channel',
    'zoho_line_items.price',
    'zoho_line_items.start_date',
    'zoho_line_items.end_date'
  ],
  filters: [
    { member: 'campaigns.stage', operator: 'equals', values: ['Campaign is Live'] },
    { member: 'zoho_line_items.is_linked_to_bravo', operator: 'equals', values: ['false'] }
  ],
  order: [['campaigns.campaign_number', 'asc']],
  limit: 500
};

const B1_VIZ: LabeledTableViz = {
  vizType: 'table',
  title: 'Unlinked Zoho Line Items',
  columns: [
    { key: 'campaigns.campaign_number', label: 'Campaign #', format: 'string' },
    { key: 'campaigns.campaign_name', label: 'Campaign Name', format: 'string' },
    { key: 'zoho_line_items.name', label: 'LI Name', format: 'string' },
    { key: 'zoho_line_items.channel', label: 'Channel', format: 'string' },
    { key: 'zoho_line_items.price', label: 'Zoho Price', format: 'currency' },
    { key: 'zoho_line_items.start_date', label: 'Start Date', format: 'date' },
    { key: 'zoho_line_items.end_date', label: 'End Date', format: 'date' }
  ]
};

const B2_QUERY: CubeQuerySchema = {
  dimensions: [
    'campaigns.campaign_number',
    'campaigns.campaign_name',
    'zoho_line_items.name',
    'zoho_line_items.channel',
    'zoho_line_items.price',
    'zoho_line_items.bravo_price',
    'zoho_line_items.price_difference',
    'zoho_line_items.start_date_matches',
    'zoho_line_items.end_date_matches'
  ],
  filters: [
    { member: 'campaigns.stage', operator: 'equals', values: ['Campaign is Live'] },
    { member: 'zoho_line_items.is_linked_to_bravo', operator: 'equals', values: ['true'] },
    { member: 'zoho_line_items.has_discrepancy', operator: 'equals', values: ['true'] }
  ],
  order: [['zoho_line_items.price_difference', 'desc']],
  limit: 500
};

const B2_VIZ: LabeledTableViz = {
  vizType: 'table',
  title: 'Linked LIs with Discrepancies',
  columns: [
    { key: 'campaigns.campaign_number', label: 'Campaign #', format: 'string' },
    { key: 'campaigns.campaign_name', label: 'Campaign Name', format: 'string' },
    { key: 'zoho_line_items.name', label: 'LI Name', format: 'string' },
    { key: 'zoho_line_items.channel', label: 'Channel', format: 'string' },
    { key: 'zoho_line_items.price', label: 'Zoho Price', format: 'currency' },
    { key: 'zoho_line_items.bravo_price', label: 'Bravo Price', format: 'currency' },
    { key: 'zoho_line_items.price_difference', label: 'Price Diff', format: 'currency' },
    { key: 'zoho_line_items.start_date_matches', label: 'Start Date Matches', format: 'string' },
    { key: 'zoho_line_items.end_date_matches', label: 'End Date Matches', format: 'string' }
  ]
};

// ─── Section C: IO Signing Status ───

const C1_QUERY: CubeQuerySchema = {
  dimensions: [
    'campaigns.campaign_number',
    'campaigns.campaign_name',
    'insertion_orders.io_number',
    'insertion_orders.name',
    'insertion_orders.sign_status',
    'insertion_orders.grand_total'
  ],
  filters: [
    { member: 'campaigns.stage', operator: 'equals', values: ['Campaign is Live'] },
    {
      member: 'insertion_orders.has_signed_document',
      operator: 'equals',
      values: ['false']
    }
  ],
  order: [['campaigns.campaign_number', 'asc']],
  limit: 500
};

const C1_VIZ: LabeledTableViz = {
  vizType: 'table',
  title: 'IOs Without Signed Documents',
  columns: [
    { key: 'campaigns.campaign_number', label: 'Campaign #', format: 'string' },
    { key: 'campaigns.campaign_name', label: 'Campaign Name', format: 'string' },
    { key: 'insertion_orders.io_number', label: 'IO Number', format: 'string' },
    { key: 'insertion_orders.name', label: 'IO Name', format: 'string' },
    { key: 'insertion_orders.sign_status', label: 'Sign Status', format: 'enum' },
    { key: 'insertion_orders.grand_total', label: 'Grand Total', format: 'currency' }
  ]
};

const C2_QUERY: CubeQuerySchema = {
  dimensions: [
    'campaigns.campaign_number',
    'campaigns.campaign_name',
    'insertion_orders.io_number',
    'insertion_orders.name',
    'insertion_orders.sign_status',
    'insertion_orders.grand_total'
  ],
  filters: [
    { member: 'campaigns.stage', operator: 'equals', values: ['Campaign is Live'] },
    { member: 'insertion_orders.sign_status', operator: 'notEquals', values: ['completed'] }
  ],
  order: [['insertion_orders.sign_status', 'asc']],
  limit: 500
};

const C2_VIZ: LabeledTableViz = {
  vizType: 'table',
  title: 'Unsigned IO Details',
  columns: [
    { key: 'campaigns.campaign_number', label: 'Campaign #', format: 'string' },
    { key: 'campaigns.campaign_name', label: 'Campaign Name', format: 'string' },
    { key: 'insertion_orders.io_number', label: 'IO Number', format: 'string' },
    { key: 'insertion_orders.name', label: 'IO Name', format: 'string' },
    { key: 'insertion_orders.sign_status', label: 'Sign Status', format: 'enum' },
    { key: 'insertion_orders.grand_total', label: 'Grand Total', format: 'currency' }
  ]
};

// ─── Dashboard Component ───

export function CampaignQa() {
  return (
    <div className="mx-auto w-full max-w-7xl space-y-10 px-6 py-6">
      <section>
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Budget Discrepancies</h2>
        <div className="mb-4">
          <VisualizationBlock
            cubeQuery={A1_QUERY}
            vizStatus="complete"
            visualization={{ query: A1_QUERY, labeledViz: A1_VIZ }}
          />
        </div>
        <VisualizationBlock
          cubeQuery={A2_QUERY}
          vizStatus="complete"
          visualization={{ query: A2_QUERY, labeledViz: A2_VIZ }}
        />
      </section>

      <section>
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Line Item Discrepancies</h2>
        <div className="mb-4">
          <VisualizationBlock
            cubeQuery={B1_QUERY}
            vizStatus="complete"
            visualization={{ query: B1_QUERY, labeledViz: B1_VIZ }}
          />
        </div>
        <VisualizationBlock
          cubeQuery={B2_QUERY}
          vizStatus="complete"
          visualization={{ query: B2_QUERY, labeledViz: B2_VIZ }}
        />
      </section>

      <section>
        <h2 className="mb-4 text-lg font-semibold text-gray-900">IO Signing Status</h2>
        <div className="mb-4">
          <VisualizationBlock
            cubeQuery={C1_QUERY}
            vizStatus="complete"
            visualization={{ query: C1_QUERY, labeledViz: C1_VIZ }}
          />
        </div>
        <VisualizationBlock
          cubeQuery={C2_QUERY}
          vizStatus="complete"
          visualization={{ query: C2_QUERY, labeledViz: C2_VIZ }}
        />
      </section>
    </div>
  );
}
